import { put, call, takeLatest, select } from 'redux-saga/effects';
import { replace } from 'connected-react-router';
import _includes from 'lodash/includes';
import _clone from 'lodash/clone';
import _isArray from 'lodash/isArray';
// import _map from 'lodash/map';
// import _get from 'lodash/get';
import _difference from 'lodash/difference';
import _isEmpty from 'lodash/isEmpty';
import ActionTypes from '../constants/ActionTypes';
import { createSolnObj } from './utils';
import {
  OPTIMIZED_SHORT_TYPE_PROBLEMS,
  CLUSTER_TYPE_PROBLEMS,
  problemTypes,
  problemTypesGroupable,
} from '../constants/ProblemTypes';
import { progressStart, progressStop } from '../actions';
import {
  getSectionWiseProblems,
  getProblemsByIds,
  // getProblemsBySlug,
} from '../api/test';
import { getApiErrorData } from '../utils/helperFunction';
import { showErrorToast } from '../actions/toast';
import { updateTestExpiryAndSessionState } from '../actions/test';
import { SESSION_STATE } from '../constants/AssessmentConstants';
import {
  DEFAULT_GROUP_LENGTH,
  CACHED_PROBLEM_GROUP_LENGTH,
} from '../constants/Constants';

/**
 * Will go through the list of problems and will prepare 2D arrays
 * A single group cannot have more than 10 problems
 * @returns {
 *  sectionGroupProbSlugs: [[],[],[]]
 *  sectionGroupProbIds: [[],[],[]]
 *  probSlugs: [],
 *  shortTypeSolution: {},
 * }
 * @param {Array} problems
 * @param {Boolean} cachedProblemData
 * @param {String} testType
 * TODO Need to check this logic
 */
const prepareGroupConfig = (problems, cachedProblemData, testType) => {
  const sectionGroupProblemSlugs = [];
  let group = [];
  let groupId = [];
  const sectionGroupProblemIds = [];
  const problemSlugs = [];
  const shortTypeSolution = {};
  // grouping the problems based on whether the problems can be groupable
  problems.forEach(problem => {
    if (testType === 'LNG') {
      if (group.length >= CACHED_PROBLEM_GROUP_LENGTH) {
        sectionGroupProblemIds.push(_clone(groupId));
        sectionGroupProblemSlugs.push(_clone(group));
        groupId = [problem.id];
        group = [problem.slug];
      } else {
        groupId.push(problem.id);
        group.push(problem.slug);
      }
    } else if (
      !_includes(problemTypesGroupable, problem.problem_type) &&
      !group.length
    ) {
      sectionGroupProblemIds.push(_clone([problem.id]));
      sectionGroupProblemSlugs.push(_clone([problem.slug]));
    } else if (
      !_includes(problemTypesGroupable, problem.problem_type) &&
      group.length
    ) {
      sectionGroupProblemIds.push(_clone(groupId));
      sectionGroupProblemSlugs.push(_clone(group));
      sectionGroupProblemIds.push([problem.id]);
      sectionGroupProblemSlugs.push([problem.slug]);
      groupId = [];
      group = [];
    } else if (
      (problem.problem_type === problemTypes.MCQ &&
        cachedProblemData &&
        group.length >= CACHED_PROBLEM_GROUP_LENGTH) ||
      (_includes(problemTypesGroupable, problem.problem_type) &&
        group.length >= DEFAULT_GROUP_LENGTH &&
        (!cachedProblemData ||
          (cachedProblemData && problem.problem_type !== problemTypes.MCQ)))
    ) {
      sectionGroupProblemIds.push(_clone(groupId));
      sectionGroupProblemSlugs.push(_clone(group));
      groupId = [problem.id];
      group = [problem.slug];
    } else if (_includes(problemTypesGroupable, problem.problem_type)) {
      groupId.push(problem.id);
      group.push(problem.slug);
    }
    problemSlugs.push(problem.slug);

    if (
      OPTIMIZED_SHORT_TYPE_PROBLEMS[problem.problem_type] &&
      problem.solution
    ) {
      shortTypeSolution[problem.slug] = {
        ...problem.solution,
        problem: { slug: problem.slug },
      };
    }
    if (CLUSTER_TYPE_PROBLEMS[problem.problem_type] && problem.children) {
      shortTypeSolution[problem.slug] = createSolnObj(problem.children);
    }
  });
  if (groupId.length) {
    sectionGroupProblemSlugs.push(_clone(group));
    sectionGroupProblemIds.push(_clone(groupId));
  }
  return {
    sectionGroupProbSlugs: sectionGroupProblemSlugs,
    sectionGroupProbIds: sectionGroupProblemIds,
    probSlugs: problemSlugs,
    shortTypeSolution,
  };
};
/**
 * We have features that hides the sections in a test. The recruiter
 * can choose to show or hide which section the problems belong to. The recruiter
 * has three options on how to show the sections
 *  1. normal sections
 *      - Candidate will not know that there are sections and he will
 *        be in the illusion that there is only one sections, even though
 *        recruiter might have created many sections
 *      - In this case the system in the background will move all problems to the
 *        first sections and problems will be grouped regardless of which section
 *        they belong to
 *  2. Normal sections but show section names
 *      - Candidate will be shown the section names on the problem list page
 *      - Problems will be grouped together regardless of whether they are from
 *        different sections
 *  2. Time based sections
 *      - Candidate will be aware that there are sections and he cannot go to another
 *        section without completing the current section
 *      - Problems will be grouped within the sections. Eg: Problems from Sections S1
 *        will not be grouped with Problems in Section S2 even though the last problem
 *        in Section S1 and the first problem in Section S2 are groupable
 * @param {Object} args
 */
function* getSections(args) {
  // const test = yield select(state => state.test);
  try {
    const test = yield select(state => state.test);
    const section = yield select(state => state.sections.sectionsNotGrouped);
    const cachedProblemData = yield select(
      state => state.test.cacheProblemData
    );
    if (section.length && !args.isForce) {
      return;
    }
    const testSlug = yield select(state => state.test.slug);

    yield put(progressStart());
    const resp = yield call(
      getSectionWiseProblems,
      args.testId,
      testSlug,
      test?.sub_test_type
    );
    const allProblems = Object.values(resp.data)
      .map(data => data.problems)
      .flat();

    const indexedProblems = allProblems.reduce(
      (idxProblemsObj, problem, index) => {
        // eslint-disable-next-line no-param-reassign
        idxProblemsObj[problem.slug] = index + 1;
        return idxProblemsObj;
      },
      {}
    );

    yield put({
      type: `${ActionTypes.INDEX_PROBLEMS}`,
      payload: {
        indexedProblems,
      },
    });
    let temp = [];
    let shortTypeSolutions = {};
    if (_isArray(resp.data) && !_isEmpty(resp.data)) {
      const isTimedSection = !_isEmpty(resp.data[0].status);
      let sections = [];
      /**
       * if the test does not have sections that are timed
       * then move all problems into 1st section
       */
      if (!isTimedSection) {
        sections = [JSON.parse(JSON.stringify(resp.data[0]))];
        resp.data.forEach((sec, index) => {
          if (index)
            sections[0].problems = sections[0].problems.concat(sec.problems);
        });
      } else sections = resp.data;

      yield put({
        type: `${ActionTypes.GET_SECTIONS}_SUCCESS`,
        payload: {
          forSolutionPage: sections,
          sectionsNotGrouped: resp.data,
        },
      });
      const { sectionsGrouped } = yield select(state => state.problems);
      if (sectionsGrouped.length) {
        return;
      }

      temp = sections.map(sec => {
        const {
          sectionGroupProbSlugs,
          sectionGroupProbIds,
          probSlugs,
          shortTypeSolution,
        } = prepareGroupConfig(
          sec.problems,
          cachedProblemData,
          test.sub_test_type
        );
        shortTypeSolutions = { ...shortTypeSolutions, ...shortTypeSolution };
        return {
          name: sec.name,
          status: sec.status,
          probSlugs,
          sectionGroupProbIds,
          sectionGroupProbSlugs,
        };
      });
    }
    if (shortTypeSolutions) {
      yield put({
        type: `${ActionTypes.SAVE_SOLUTION}_LIST_SUCCESS`,
        payload: shortTypeSolutions,
      });
    }

    yield put({
      type: `${ActionTypes.SET_PROBLEMS_GROUP}_SUCCESS`,
      payload: temp,
    });
  } catch (e) {
    const { toast, isTestSubmitted, slug } = yield select(state => ({
      toast: state.appStatus.toast,
      isTestSubmitted: state.test.isTestSubmitted,
      slug: state.test.slug,
    }));
    const errorInfo = getApiErrorData(e, 'GSP');
    if (
      errorInfo &&
      errorInfo.message &&
      !toast?.apiErrorInfo &&
      !isTestSubmitted
    ) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
    if (e.response?.data?.action?.redirection) {
      yield put(
        updateTestExpiryAndSessionState({
          is_expired: true,
          session_state: SESSION_STATE.END,
        })
      );
      yield put(replace(`/test/${slug}/feedback`));
    }
    // const message = _get(e, 'response.data', null);
    // const hasCustomForm = _get(test, 'settings.hacker_extra_data.custom_data.enabled', null);
    // const { is_expired, slug: testSlug, is_onboarded } = test;
    // if (
    //   is_expired === true &&
    //   message === 'Your test session has ended.'
    // ) {
    //   yield put(replace(`/test/${testSlug}/feedback`));
    // } else if (hasCustomForm && !is_onboarded) {
    //   yield put(replace(`/${testSlug}/onboarding/form`));
    // } else if (hasCustomForm && is_onboarded) {
    //   yield put(replace(`/test/${testSlug}`));
    // } // @Todo add condition for onboarding pages here
  } finally {
    yield put(progressStop());
  }
}
function* getProblems(args) {
  // const test = yield select(state => state.test);
  try {
    const test = yield select(state => state.test);
    const cachedGroups = yield select(state => state.problems?.cached || []);
    const cachedProblems = Array.isArray(cachedGroups)
      ? cachedGroups.flatMap(g =>
          Array.isArray(g?.problems) ? g.problems : []
        )
      : [];
    const cachedIds = cachedProblems
      .map(p => p?.id)
      .filter(id => id !== undefined && id !== null)
      .map(id => String(id));

    const requestedIds = (args.problemIds || [])
      .filter(id => id !== undefined && id !== null)
      .map(id => String(id));

    const newProblemsIds = _difference(requestedIds, cachedIds);
    yield put(progressStart());
    if (newProblemsIds.length) {
      const resp = yield call(
        getProblemsByIds,
        args.testId,
        newProblemsIds,
        test?.sub_test_type === 'ADP' ? test.sub_test_type : undefined
      );
      const fromIds = Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp?.data?.results)
        ? resp.data.results
        : [];

      // ADP fallback: some backends may not return anything for the id-based list endpoint.
      // If we have slugs, fetch those directly to avoid an infinite retry loop in UI.
      const finalProblems = fromIds;

      if (finalProblems.length) {
        yield put({
          type: `${ActionTypes.GET_PROBLEMS}_SUCCESS`,
          payload: finalProblems,
        });
      }
    }
  } catch (e) {
    const { toast, slug } = yield select(state => ({
      toast: state.appStatus.toast,
      slug: state.test.slug,
    }));
    const errorInfo = getApiErrorData(e, 'GBP');
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
    if (e.response?.data?.action?.redirection) {
      yield put(
        updateTestExpiryAndSessionState({
          is_expired: true,
          session_state: SESSION_STATE.END,
        })
      );
      yield put(replace(`/test/${slug}/feedback`));
    }
    // const message = _get(e, 'response.data', null);
    // if (
    //   test.is_expired === true &&
    //   message === 'Your test session has ended.'
    // ) {
    //   yield put(replace(`/test/${test.slug}/feedback`));
    // } else {
    //   yield put(replace(`/test/${test.slug}`));
    // }
  } finally {
    yield put(progressStop());
  }
}
export default function* loadSectionsSaga() {
  yield takeLatest(ActionTypes.GET_PROBLEMS, getProblems);
  yield takeLatest(ActionTypes.GET_SECTIONS, getSections);
}
